import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { FinanceBadge } from "../financeUtils";

const PAGE_SIZE = 10;
const EMPTY_FORM = {
  name: "",
  category: "",
  contactName: "",
  email: "",
  phone: "",
  gstNumber: "",
  paymentTerms: "",
  status: "Active",
};

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      const res = await apiFetch(`/api/finance/vendors?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load vendors.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(vendors.length / PAGE_SIZE));
  const pagedVendors = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return vendors.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [vendors, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function openCreate() {
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  async function createVendor(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/finance/vendors", {
        method: "POST",
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create vendor.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="finance-page">
      <PageHeader
        title="Vendors"
        subtitle={`${vendors.length} vendor${vendors.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>Add Vendor</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search vendor, category, contact" />
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card finance-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>{[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}</div>
        ) : vendors.length === 0 ? (
          <EmptyState title="No vendors yet" subtitle="Add vendors for invoices, expenses, and admin approvals." action={<button className="primary" onClick={openCreate}>Add Vendor</button>} />
        ) : (
          <>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Category</th>
                    <th>Contact</th>
                    <th>GST</th>
                    <th>Payment Terms</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedVendors.map((vendor) => (
                    <tr key={vendor.id}>
                      <td>
                        <div className="finance-row-title">{vendor.name}</div>
                        <div className="finance-row-subtitle">{vendor.email || ""}</div>
                      </td>
                      <td>{vendor.category || "-"}</td>
                      <td>
                        <div>{vendor.contactName || "-"}</div>
                        <div className="finance-row-subtitle">{vendor.phone || ""}</div>
                      </td>
                      <td>{vendor.gstNumber || "-"}</td>
                      <td>{vendor.paymentTerms || "-"}</td>
                      <td><FinanceBadge status={vendor.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={vendors.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createVendor} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Vendor</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              {[
                ["name", "Vendor Name", true],
                ["category", "Category"],
                ["contactName", "Contact Name"],
                ["email", "Email"],
                ["phone", "Phone"],
                ["gstNumber", "GST Number"],
                ["paymentTerms", "Payment Terms"],
              ].map(([field, label, required]) => (
                <label key={field}>
                  {label}
                  <input value={form[field]} onChange={(event) => updateField(field, event.target.value)} required={Boolean(required)} />
                </label>
              ))}
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Add Vendor"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
